/**
 * @author Lorenzo Muscariello
 * @date 02/05/2022
 */
//var Utils = require("../ServerUtils");


import { DetectedWebView, detectWebView } from "@ciaopeople/cp-shatsu/ClientDetection";
import { OuterResolvablePromise, PromiseOnSteroids } from "@ciaopeople/cp-shatsu/PromiseOnSteroids";
import { isObject } from "@ciaopeople/cp-shatsu/TypeChecking";
import { CpNativeAppIntegration, NativeAppSetSessionCause } from "../nativeapps/CpNativeAppIntegration.js";
import { APIClientInterface } from "../api/APIClientInterface.js";
import { DataDeletionInfo } from "../api/types/AuthDataTypes.js";
import { SessionObject } from "../session/SessionObject.js";
import { SessionStorageInterface } from "../session/SessionStorageInterface.js";
import { getUserIdFromSession, StorableSession, StorableSessionInfo } from "../session/StorableSession.js";
import { StatsCollector } from "../stats/StatsCollector.js";
import { AuthProviderIdentifier } from "./AuthProviderIdentifier.js";
import { LoginError, LoginErrorCodes, ThirdPartyCientAuthData, ThirdParyClientAuthDataForSignIn } from "./data/LoginData.js";
import { FirstPartyEmailLogin } from "./direct_providers/FirstPartyEmailLogin.js";
import { LoginProvider, PrompLoginCustomParameters } from "./direct_providers/LoginProvider.js";
import { DirectLoginManagerConfig } from "./DirectLoginManagerConfig.js";
import { debug } from "./LoginDbg.js";
import { LoginManagerBaseImpl } from './LoginManager.js';
import { LoginMode } from "./LoginMode.js";
import { UserRole } from "./UserRole.js";
import { DeviceIdProvider } from "@api-commons/utils/DeviceIdProvider.js";
import { getDeviceNameFromDetectedWebview } from "@api-commons/api/types/SubscriptionTypes.js";
import { QUEUE_PARAM_NAME } from "@api-commons/sdk/LoaderConstants.js";




function standard_autologin_chain(): AuthProviderIdentifier[] {
    return [
        AuthProviderIdentifier.FIRST_PARTY_EMAIL,
        AuthProviderIdentifier.FACEBOOK,
        AuthProviderIdentifier.GOOGLE
    ];
}


type ProviderMap = { [k in keyof AuthProviderIdentifier]?: LoginProvider };


const NoSessionObject = new SessionObject(null, null);

/**
 * this implementation of login manager uses Firebase as intermediate
 * provider and several (as configured from outside) actual providers
 * 
 * 
 * config structure 
 *      {
 *          firebase: {... firebase configuration},
 *          storage:  {... cookie names and domain},
 *          general: {
 *              app_token: $applicationToken
 *          }
 *          autologin: {
 *              enabled: true/false,
 *              chaingen: Context => [...providers], -> autologin provider priroity chain generator
 *              retrieveActualDataAlways: true/false -> if false stops on intermediate data receival
 *          }
 *      }
 * 
 * 
 * 
 * where context is: 
 *      {
 *          platform: {
 *              ios: true/false
 *              android: true/false
 *              other: true/false
 *          },
 *          browser: {
 *              safari: true/false,
 *              chrome: true/false,
 *              webview: true/false,
 *              stock: true/false
 *          },
 *          native_app: true/false,
 *          extra: {
 *              androidVersion: $androidVersion,
 *              webkitVersion: $wkitVersion,
 *              product: $ciaopeopleProduct,
 *              nativeAppVersion: $ciaopeople_native_app_version,
 *              externalApp: "facebook"/"twiter"/null
 *          }
 *      },
 *      
 * 
 * @param {*} config 
 * @param {*} providers a list of providers
 */

interface RevokeData {
    hint: string,
    providers: AuthProviderIdentifier[],
    isDeletion: boolean
}


class DirectLoginManager extends LoginManagerBaseImpl {
  
    private providerMap: ProviderMap = {};
    private detectedWebview: DetectedWebView;
    private session: StorableSession = null;
    private lastLoginSessionState: string = "";
    private appToken: string;
    private autologinChainGen: () => AuthProviderIdentifier[] = standard_autologin_chain;
    private startAlreadyInvoked: boolean;
    private nativeAppLoginWaiters: OuterResolvablePromise<SessionObject>[];

    constructor(public readonly config: DirectLoginManagerConfig, 
        private readonly providers: LoginProvider[],
        public readonly sessionHelper: SessionStorageInterface,
        public stats: StatsCollector,
        public api: APIClientInterface,
        public nativeAppIntegration: CpNativeAppIntegration,
        public deviceIdProvider: DeviceIdProvider
    ) {
        super();
        this.detectedWebview = detectWebView();
        providers.forEach(prov => this.providerMap[prov.providerId] = prov);
        if(config.autologin && config.autologin.chaingen) {
            debug.write("Overriding STD_AC with chaingen", config.autologin);
            this.autologinChainGen = config.autologin.chaingen;
        }
        else {
            debug.write("Using STD_AC");
        }
        this.appToken = config.general.app_token;
        this.startAlreadyInvoked = false;
        this.nativeAppLoginWaiters = [];
    }


    webviewDetect(): DetectedWebView {
        return this.detectedWebview;
    }


    public getNativeAppLoginPromise(): Promise<SessionObject> {
        const resolvable = PromiseOnSteroids.getResolvable<SessionObject>();
        this.nativeAppLoginWaiters.push(resolvable);
        return resolvable.promise;
    }
  

    private cleanResolve(): void {        
        this.nativeAppLoginWaiters = [];
    }


    public preloadProviders(ids: AuthProviderIdentifier[], async: boolean): Promise<void> {
        const reval: Promise<void> = Promise.all(this.getProviderListSync().filter(el => ids.findIndex(ex => ex == el.providerId) >= 0).map(el => el.preloadAction())).then();
        if(async) return Promise.resolve();
        else return reval;
    }
    

    public getProviderList(): Promise<AuthProviderIdentifier[]> {
        return Promise.resolve(this.getProviderListSync().map(el => el.providerId));
    }
    
    public getProviderListSync(): LoginProvider[] {
        return this.providers;
    }


    private produceRevokeData(isDeletion: boolean): Promise<RevokeData> {
        if(!this.detectedWebview.native_app) {
            if(this.config.revokeOnClientSideOnDeletion) {
                return this.api.getMe(true).then(user => {
                    const userdata = user.data;
                    const provlist = userdata.loginProviderInfo?userdata.loginProviderInfo.map(el => el.providerId):[];
                    const hint = userdata.email;
                    return {
                        hint: hint,
                        providers: provlist,
                        isDeletion: isDeletion
                    };
                });
            }
            else {
                debug.write("Producing Null revoke data ... revoke on client side is not enabled");
                return Promise.resolve(null);
            }
        }
        else {
            debug.write("Producing Null revoke data ... is native app");
            return Promise.resolve(null);
        }

    }


    private eventuallyRevokeInternal(rvk: RevokeData): Promise<boolean> {
        if(rvk && rvk.providers && rvk.providers.length > 0) {
            return this.eventuallyRevoke(rvk.hint, rvk.providers, rvk.isDeletion);
        }
        else {
            debug.write("Null revoke data - nothing to resolve");
            return Promise.resolve(false);
        }
    }


    public eventuallyRevoke(hint: string, providers: AuthProviderIdentifier[], isDeleteRequest: boolean): Promise<boolean> {
        debug.write("revoke Incoming providers: ", providers);
        const mapped = providers.reduce((acc, el) => {
            acc[el] = true;
            return acc;
        },
        {});
        debug.write("mapped providers", mapped);
        const useProviders = this.providers.filter(el => mapped[el.providerId]);
        debug.write("Providers to use in revoke: ", useProviders);
        const mainProm: Promise<boolean> = Promise.all(useProviders.map(prov => {
            return prov.eventuallyHandleRevoke(hint, isDeleteRequest);
        })).then(resp => {
            return resp.filter(e => !e).length <= 0;
        });
        return Promise.race<boolean>([mainProm, 
            PromiseOnSteroids.timeout<boolean>(10000, false)]);
    }
  


    /**
     * Questo evento viene utilizzato internamente per aggiornare le informazioni
     * sulla sessione corrente. Se viene individuato un cambio nella sessione
     * viee lanciato un evento __loginstatuschange__
     * Il queue resolver associato al login manager viene inizializzato se non lo è
     * ancora.
     */
    private changeSessionStatus(session: StorableSession, info: StorableSessionInfo | null): Promise<void> {
        this.session = session;

        const newUser = info && info.isNewUser ? info.isNewUser : false;
                
        const newToken = this.session?this.session.token:null;
        debug.write("Setting token ... " + newToken);
        debug.write("Status Change with incoming data", session, info);
        
        const newStatus = session && session.role === UserRole.AUTHENTICATED_USER?"1":"0";
        const prevState = this.lastLoginSessionState +"";
        // verifica se esiste un cambio di stato ed eventualmente 
        // marca sul collettore delle statistiche (GA3) l'utente rispetto allo stato di login
        const promises: Promise<any>[] = [];

        if(newStatus != this.lastLoginSessionState) {

            this.lastLoginSessionState = newStatus;
            promises.push(this.sessionHelper.setLoginPreviousState(newStatus === "1"));

            const providerId = info && info.provider ? info.provider : "UNK";

            const loginMode = info && info.loginMode ? info.loginMode : LoginMode.MANUAL;

           
            
            if(newStatus === "1") {
                 const user_id = getUserIdFromSession(session);
                if(user_id) {
                    this.nativeAppIntegration.nativeapp.setAnalyticsUserProperties({user_id: user_id});
                }
                if (providerId == "UNK") {
                    debug.write("WARN! UNK provider detected on changeSessionStatus, info was: ", info);
                }

                if ( ! (loginMode == LoginMode.SESSION && providerId == "UNK") ) {
                    promises.push(this.stats.markLoginStatus(true, providerId));
                    promises.push(this.stats.loginDone(providerId, loginMode, newUser));
                }
            }
            else if(newStatus === "0" && prevState == "-") {
                promises.push(this.stats.markLoginStatus(false, providerId));
                debug.write("Anon user ... no event to push");
            }
            else {
                this.nativeAppIntegration.nativeapp.setAnalyticsUserProperties({user_id: null});
                promises.push(
                    this.sessionHelper.getLastLoginProvider().then(lprov => {
                        return Promise.all([
                            this.stats.markLoginStatus(false, lprov as AuthProviderIdentifier),
                            this.stats.logoutDone(lprov as AuthProviderIdentifier)
                        ]).then();
                    })
                );
            }

        }
        //aggiorno il token del client col nuovo token o con l'app token (in caso di logout)
        return Promise.all(promises).then(_ => {
            return this.api.updateToken(newToken?newToken:this.appToken).then(_x => {
                debug.write("triggering loginstatuschange");
                //triggers event in order to propage session status change to let know to the rest of the application
                
                this.triggerLoginStatusChange(session);
        
                //setting Login manager as ready if it was not
                if(!this.isReady()) this.setReady(this);
            });
        });
    }

    /**
     * checks if resumed session is a valid one
     * @param {*} resumedSession 
     */
    private getAndCheck(resumedSession: StorableSession): Promise<SessionObject> {
        debug.write("Checking Resumed Session");
        return this.sessionHelper.getLastLoginProvider().then(
            provider => {
                debug.write("Retrieved provider in get and Check is", provider);
                return this.resumeSessionByProvider(provider as AuthProviderIdentifier, resumedSession, NativeAppSetSessionCause.SESSION_REFRESH, false);
            }
        );
    }

    private resumeSessionByProvider(provider: AuthProviderIdentifier, resumedSession: StorableSession, cause: NativeAppSetSessionCause = NativeAppSetSessionCause.SESSION_REFRESH, isNewUser: boolean = false): Promise<SessionObject> {
        // @task #10063
        // @task #10764
        const sessionInfo: StorableSessionInfo = {
            provider: provider,
            loginMode: cause == NativeAppSetSessionCause.LOGIN ? LoginMode.MANUAL : LoginMode.SESSION,
            isNewUser: isNewUser
        };
        let testGrantPromise = null;
        if(resumedSession && resumedSession.token) {
            testGrantPromise = this.api.testGrant(resumedSession.token).then(
                (res) => {
                    debug.write("Remote session check response", res);
                    let useSession = null;
                    if(res.data && res.data.valid) {
                        debug.write("Session is valid pushing status change");
                        useSession = resumedSession; 
                    }
                    else {
                        debug.write("Session was not valid ... clearing");
                        this.sessionHelper.clearSession();
                    }
                    debug.write("resumeSessionByProvider sending out: ", useSession, sessionInfo);
                    return new SessionObject(useSession, sessionInfo);
                }
            );
        }
        else {
            testGrantPromise = Promise.resolve(new SessionObject(null, sessionInfo));
        }
    
        return this.changeSessionStatusFromPromise(testGrantPromise);
    }


    /**
     * private method to handle properly change session status
     * on promise output
     * the input promise must return an object with this properties
     * {
     *      session: {sessionobj} *nullable,
     *      sessionInfo: {session_info_object} *nullable
     * }
     * @param {} promise 
     * @returns a new promise with changeSessionStatusHandled
     */
    private changeSessionStatusFromPromise(sessionDataPromise: Promise<SessionObject>, forward_errors: boolean = false): Promise<SessionObject> {
        
        return sessionDataPromise.then(dataobj => {
            let changeSessionProm: Promise<void> = Promise.resolve();

            if(!dataobj.change_already_handled) {
                changeSessionProm = this.changeSessionStatus(dataobj.session, dataobj.sessionInfo);
            }
            else {
                debug.write("Session Change promise already handled ... or not valid session object passed skipping");
            }
            return changeSessionProm.then(() => {
                return new SessionObject(
                    dataobj.session,
                    dataobj.sessionInfo,
                    true
                );
            });
        }).catch( (err) => {
            debug.write("There was an error checking session", err);
            return this.changeSessionStatus(null, null).then(() => {
                if(forward_errors) {
                    return Promise.reject(err);
                }
                else return new SessionObject(
                    null,
                    null,
                    true
                );
            });
        });

    }



    /**
     * starts login manager and does checks as explained in 
     * flux diagram `docs/flusso_login_firebase.pdf`
     * 
     */
    public start(): void {
        if(this.startAlreadyInvoked) {
            debug.write("Login Manager already started");
            return;
        }
        this.startAlreadyInvoked = true;
        debug.write("Startin Direct login manager");
        this.sessionHelper.getLoginPreviousState().then(
            last => {
                this.lastLoginSessionState = last;
                return true;
            }
        ).then(_sHelperInit => {
            debug.write("Checking session status");
            const sessPromise: Promise<SessionObject> = this.sessionHelper.isAutologinLocked().then(autologinLocked => {
                debug.write("looking for first party providers");
                const firstPartyProvider = this.providers.find(e => {
                    console.log("class of e",e);
                    return e instanceof FirstPartyEmailLogin
                });
                debug.write("Found? " + (firstPartyProvider?firstPartyProvider.providerId:"NO") );
                const maybeFirstPartyLoginData = firstPartyProvider?FirstPartyEmailLogin.searchForVerificationData():null;
                if(maybeFirstPartyLoginData) {
                    debug.write("found 1st party login data ... queuing login");
                    window[QUEUE_PARAM_NAME] = window[QUEUE_PARAM_NAME] || [];
                    window[QUEUE_PARAM_NAME].push(() => {
                        debug.write("dequeuing 1st party login");
                        this.changeSessionStatusFromPromise(this.serverSignInPromise(maybeFirstPartyLoginData, false).then(
                            res => {
                                this.sessionHelper.unlockAutologin()
                                return res;
                            }
                        ));
                    });
                    return new SessionObject(null,null,false);
                    
                }
                else {
                    if(!autologinLocked) {
                        //user did not execute explicit logout
                        debug.write("Autologin is not locked");
                        return this.sessionHelper.getSession().then(
                            resumedSession => {
                                if(isObject(resumedSession)) {
                                    //session retrieved from storage is an object going to check
                                    debug.write("Resuming Session", resumedSession);
                                    return this.sessionHelper.shouldUpdateCookies().then(shouldUpdateCookies => {
                                        if(shouldUpdateCookies) {
                                            debug.write("[SS] Should update cookies");
                                            this.sessionHelper.storeSession(resumedSession);
                                            this.sessionHelper.getLastLoginProvider().then(
                                                llp => {
                                                    if (llp && llp != "UNK") {
                                                        this.sessionHelper.setLastLoginProvider(llp);
                                                    }
                                                }
                                            );
                                            this.sessionHelper.cookieVersionStore();
                                        }
                                        else debug.write("[SS] nothing to update");
                            
                                        debug.write("Going to check resumed session");
                                        return this.getAndCheck(resumedSession);
                                    });
                                    
                                }
                                else {
                                    debug.write("No session to restore running autologin procedure");
                                    return this.chainedAutologinPromise();
                                   
                                }
                            }
                        );
                    }
                    else {
                        //autologin in inhibited by explicit user logout
                        debug.write("Autologin Prevented by user choice");  
                        return Promise.resolve(NoSessionObject);
                    }
                }
                
            });
           
            this.changeSessionStatusFromPromise(sessPromise);
        });       
    }
    

    /**
     * private method used to try execute autologin
     * 
     * - check if explicit user logout has been done
     * - check if there's an existing session
     * - check if 3rd party actual providers have an existing session
     * - eventually execute signin procedure on server
     * 
     * --- does not execute session status change routine ... 
     * this must be done from the caller
     */
    private chainedAutologinPromise(): Promise<SessionObject> {
        
        
        const al_providers = this.autologinChainGen().map(id => {
            return this.providerMap[id];
        }).filter(prov => {
            
            return prov && prov.supportsAutoLogin;
        });
        debug.write("Autologin chain is", al_providers);

        const prom: Promise<ThirdPartyCientAuthData|null> = Promise.resolve(null);
        
        return al_providers.reduce((prevPromise, actualProvider) => {
            return prevPromise.then((prevResult) => {
                debug.write("Previous Provider Result ", prevResult);
                //if we got result from previous provider - return
                if(prevResult !== null) {
                    return prevResult;
                }
                else {
                    //try next provider
                    debug.write("Trying autologin with provider " + actualProvider.providerId);
                    return actualProvider.getSessionDataForAutologin().then(res => {
                        debug.write("Got Result", res);
                        return res;
                    }).catch(err => {
                        debug.write("Got Error", err);
                        return Promise.resolve(null);
                    });
                }
            });
        }, prom).then((tpd) => {
            //finally send data to server for signin procedure 
            if(tpd !== null) {
                debug.write("Finally we have this third party user data", tpd);
                return this.serverSignInPromise(tpd, true);
            }
            else return Promise.resolve(NoSessionObject);
            
        }).catch(err => {
            debug.write("Something wrong in autologin", err);
            return Promise.resolve(NoSessionObject);
        });
    }

    public manualSignInFromThirdPartyClientAuthData(tpud: ThirdPartyCientAuthData) {
        return this.serverSignInPromise(tpud, false);
    }

    public nativeAppSignIn(token: string, tpud: ThirdPartyCientAuthData): Promise<void> {
        return this.serverSignInPromise(tpud, false).then(
            sess => {
                console.log("Native app init cleanup after login");
                const p = Promise.all([
                    this.sessionHelper.unlockAutologin(),
                    this.changeSessionStatus(sess.session, sess.sessionInfo)
                ]).then(
                    () => {
                        console.log("Native app complete cleanup after login");
                        return sess;
                    }
                );
                this.nativeAppLoginWaiters.forEach(r => r.chainResolve(p));
                p.finally(() => {
                    this.cleanResolve()
                });
                return p.then();
            }
        );
    }



    private async enrichTPUDWithDeviceInfo(tpud: ThirdPartyCientAuthData): Promise<ThirdPartyCientAuthData> {
        let deviceId = null;
        let deviceName = null;
        if(this.detectedWebview.native_app) {
            const devInfo = await this.nativeAppIntegration.nativeapp.getNotificationDeviceInfo();
            if(devInfo.device?.device_id) {
                deviceId = devInfo.device.device_id;
                deviceName = devInfo.device.device_name;
            }
            
        }
        else {
            deviceId = await this.deviceIdProvider.retrieveOrGenerateDeviceId().then(e => e?e.deviceId:null);
            deviceName = getDeviceNameFromDetectedWebview(this.detectedWebview);
        }
        
        if(deviceId) {
            return ThirdPartyCientAuthData.FromInterface({
                actualData: tpud.actualData,
                signInParameters: tpud.signInParameters,
                intermediateData: tpud.intermediateData,
                deviceInfo: {
                    deviceId: deviceId,
                    deviceName: deviceName?deviceName:""
                }
            });
        }
        else return tpud;
        
    }

    /**
     * this is a private method, used to handle signIn handshake with
     * REST API server
     * @param {*} loginData 
     * @returns 
     */
    private async serverSignInPromise(tpud: ThirdPartyCientAuthData, isAutologin: boolean) {
        
        if(tpud !== null) {
            const withDeviceInfo = await this.enrichTPUDWithDeviceInfo(tpud);
            const tpudUpgraded = new ThirdParyClientAuthDataForSignIn(withDeviceInfo, window.location.href);
            debug.write("Upgraded tpud:", tpudUpgraded);
            return this.api.signIn(this.appToken, tpudUpgraded).
                then(auth_response => {
                    debug.write("Authentication Response is", auth_response);
                    return this.api.updateToken(auth_response.data.access_token).then(_x => {
                        debug.write("Token Updated", auth_response);
                        const realProvider = tpud.actualData.providerId;
                        if (auth_response && auth_response.status == 200 && 
                            auth_response.data && auth_response.data.access_token) {
                            //got valid data from server
                            const storableSession = {
                                token: auth_response.data.access_token,
                                role: UserRole.AUTHENTICATED_USER
                            };
                            debug.write("Storing session");
                            return this.sessionHelper.storeSessionAndProvider(storableSession, realProvider).then(_ => {
                                return new SessionObject(storableSession, {
                                    loginMode: isAutologin? LoginMode.AUTO: LoginMode.MANUAL,
                                    provider: realProvider,
                                    isNewUser: !!auth_response.data.isNewUser
                                });
                            })
                            
                        }
                        else {
                            //got invalid data from server
                            debug.write("Got invalid auth_data from server");
                            return NoSessionObject;
                        }
                    });
                    
                });
        }
        else return Promise.reject(new LoginError(LoginErrorCodes.NO_SESSION_FOUND, "Missing session data, could not execute server signIn"));
    }



    /**
     * tries autologin or login for specific provider
     * @param providerId specific provider
     * @returns 
     */
    public loginOrAutologin(providerId: AuthProviderIdentifier, customDetails: PrompLoginCustomParameters = null): Promise<SessionObject> {
        if(providerId in this.providerMap) {
            const fProv: LoginProvider = this.providerMap[providerId];
            debug.write("LorA - going ahead with provider", providerId);
            (fProv.supportsAutoLogin?fProv.getSessionDataForAutologin():Promise.resolve(null)).
                then(loginData => {
                    if(loginData != null) {
                        debug.write("LorA - We got valid response from autologin", loginData);
                        loginData.isAutologin = true;
                        return loginData;
                    }
                    else return Promise.reject({});
                }).catch(err => {
                    debug.write("LorA- Got Error on autologin", err);
                    return fProv.promptLogin(customDetails); 
                }).then((tpd: ThirdPartyCientAuthData & { isAutologin?: boolean }) => {
                    if(tpd !== null) {
                        debug.write("LorA - Finally we have this third party user data", tpd);
                        return this.serverSignInPromise(tpd, tpd.isAutologin?true:false);
                    }
                    else Promise.reject(new LoginError(LoginErrorCodes.NO_SESSION_FOUND, "Missing login data", tpd));
                }).catch(err => {
                    debug.write("LorA - there was an error", err);
                    return Promise.resolve(NoSessionObject);
                });
            
        }
        else {
            debug.write("LorA - Missing provider with id", providerId);
            return Promise.reject("Missing provider " + providerId + " loginOrAutologin");
        }
    }




    /**
     * Routine comune alla gestione del login esplicito via popup o onetap
     * questo metodo è ad uso interno
     * @param {*} providerId identificativo del provider
     * @param {*} extra_params 
     * @param {*} loginMethod funzione che estrae il metodo di login a partire dal provider
     */
    private internalLoginRoutine(providerId: AuthProviderIdentifier, extra_params: any, loginMethod: (LoginProvider) =>  Promise<ThirdPartyCientAuthData>, forward_errors: boolean = false): Promise<SessionObject> {
        const signInParams = convertExtraParams(extra_params);
        
        if(providerId in this.providerMap) {
            const fProv = this.providerMap[providerId];
            debug.write("Asked Login with Provider", fProv.providerId);
            return this.changeSessionStatusFromPromise(loginMethod(fProv).then(tpd => {

                const wasOneTapAutoSelect = tpd.signInParameters && tpd.signInParameters['oneTapAutoSelect'] === true?true:false;
                
                debug.write("After login there was", tpd);
                if(tpd !== null) {
                    if(signInParams) tpd.signInParameters = signInParams;
                    return this.serverSignInPromise(tpd, wasOneTapAutoSelect).then(
                        res => {
                            this.sessionHelper.unlockAutologin();
                            return res;
                        }
                    );
                }
                else return Promise.reject(new LoginError(LoginErrorCodes.NO_SESSION_FOUND, "No login was done", tpd));
            }).catch(error => {
                debug.write("There was an error", error);
                if(forward_errors) {
                    return Promise.reject(error);
                }
                return NoSessionObject;
            }), forward_errors);
        }
        else return Promise.reject( { err: "MissingData" } );
    }

    public loginWithTPUD(tpud: ThirdPartyCientAuthData, extra_params: any): Promise<SessionObject> {
        return this.internalLoginRoutine(tpud.actualData.providerId, extra_params, (_fProv) => Promise.resolve(tpud));
    }

    /**
     * Login esplicito
     * @param {*} providerId identificativo del provider
     * @param {*} extra_params 
     */
    public login(providerId: AuthProviderIdentifier, extra_params: any, customParams: PrompLoginCustomParameters = null):  Promise<SessionObject> {
        return this.internalLoginRoutine(providerId, extra_params, (fProv) => fProv.promptLogin(customParams));
    }
 
    /**
     * Login esplicito via one tap
     * @param {*} providerId identificativo del provider
     * @param {*} extra_params 
     */
    public doOneTapLogin (providerId: AuthProviderIdentifier, extra_params: any):  Promise<SessionObject> {
        return this.internalLoginRoutine(providerId, extra_params, (fProv) => fProv.oneTapLogin(), true);
    }
 


  
     //?TODO: #13144 must be removed
    /**
     * this method allows native apps to inject the session from outside the webview
     * @deprecated
     * @param {*} token 
     * @returns 
     */
    public nativeAppSetSession(token: string): Promise<SessionObject> {
        
        debug.write("!!! Call to deprecated method 'nativeAppSetSession' detected !!!");
        debug.write("[NAPP] setting session from native app - tok:", token);
        return this.resumeSessionByProvider(AuthProviderIdentifier.FACEBOOK, { token: token, role: UserRole.AUTHENTICATED_USER }, NativeAppSetSessionCause.SESSION_REFRESH, false).then(
            sess => {
                return Promise.all([
                    this.sessionHelper.unlockAutologin(),
                    this.sessionHelper.storeSession(sess.session)
                ]).then(_x => sess);
            }
        );
    }

     //?TODO: #13144 must be removed
    /**
     * this method allows native apps to inject the session from outside the webview
     * @param {*} token 
     * @returns 
     */
    public nativeAppSetSessionWithProvider(provider: AuthProviderIdentifier, token: string, cause: NativeAppSetSessionCause, isNewUser: boolean): Promise<SessionObject> {
        
       
        const unlockPromise = cause == NativeAppSetSessionCause.LOGIN? this.sessionHelper.unlockAutologin() : Promise.resolve();
        
        return unlockPromise.then(_r => {
            return this.resumeSessionByProvider(provider, { token: token, role: UserRole.AUTHENTICATED_USER }, cause, isNewUser).then(
                sess => {
                    return this.sessionHelper.storeSession(sess.session).then(_ => {
                        if (sess && sess.sessionInfo && sess.sessionInfo.provider) {
                            return this.sessionHelper.setLastLoginProvider(sess.sessionInfo.provider).then(
                                _ => sess
                            );
                        }
                        else {
                            return sess;
                        }
                    });
                }
            );
        });
    }

    /**
     * indentifica se i cookie sono stati mai settati per la sessione
     * @method thereWasASession
     *
     */
    public thereWasASession(): Promise<boolean> {
        return this.sessionHelper.thereWasASession();
    }


    /**
     * Torna true se è un login inibito
     */
    public isAutologinLocked(): Promise<boolean> {    
        return this.sessionHelper.isAutologinLocked();
    }

    /**
     * Questo metodo consente di allineare, se si è verificato un cambiamento
     * nello stato del login in un contesto diverso da quello di esecuzione dello
     * script (altra pagina, altra tab).
     * Se si è verificato un cambiamento verrà lanciato come sempre un evento `loginStatusChanged`
     */
    public checkIfSessionChanged(): Promise<SessionObject> {
        const checkPromise: Promise<SessionObject> = Promise.all([
            this.sessionHelper.isAutologinLocked(),
            this.sessionHelper.getSession()
        ]).then(
            ([
                autologinLocked,
                resumedSession
            ]) => {
                if (!autologinLocked) {
                    if (isObject(resumedSession)) {
                        return this.getAndCheck(resumedSession);
                    }
                    else {
                        return NoSessionObject;
                    }
                }
                else {
                    return NoSessionObject;
                }
            }
        );
        return this.changeSessionStatusFromPromise(checkPromise);
    }

    /**
     * Questo metodo effettua il logout:
     * Distruzione della sessione
     * Inibizione dell'autologin
     */
    public logout(): Promise<void> {
        const revokeData: Promise<RevokeData> = this.produceRevokeData(false);
        this.session = null;
        //this.sessionHelper.clearSession();
        this.sessionHelper.blockAutologin();
        return this.changeSessionStatus(null, null).then(()=> {
            return this.api.updateToken(this.appToken).then(
                _r => {
                    return revokeData.then( rvk => {
                        return this.eventuallyRevokeInternal(rvk);
                    });
                }
            ).then();
        });
    }

    public dPD(checkField: string): Promise<DataDeletionInfo> {
        const revokeData: Promise<RevokeData> = this.produceRevokeData(true);
        return this.api.deletePersonalData(checkField).then(response => response.data).
            then(cacheRes => {
                return this.sessionHelper.blockAutologinLonger().
                    then(_x => cacheRes).
                    catch(_e => cacheRes);
            }).then(delResponse => {
                if(delResponse && delResponse.confirmation_code) {
                    return revokeData.then(rvk => {
                        return this.eventuallyRevokeInternal(rvk);
                    }).then(_ => delResponse);
                }
                else return delResponse;
            }).then(
                c => {
                    this.sessionHelper.clearSession();
                    return c;
                }
            );
    }


    public getLastLoginProvider(): Promise<string> {
        return this.sessionHelper.getLastLoginProvider();
    }

}

/**
 * internal method used to format extra parameters to pass to the server
 * for sign in procedure
 * Json.format of this object is explained in SignInParameters scala class
 */
function convertExtraParams(extra_params: any) {
    const formatted: any = {};
    if(extra_params) {
        if(extra_params.enable_bsim !== undefined) {
            formatted.birthdaySimulation = extra_params.enable_bsim?extra_params.enable_bsim:false;
            delete extra_params['enable_bsim'];
        }
        if(extra_params.enable_gender_sim && extra_params.enable_gender_sim != "" ) {
            formatted.userGenderSimulation = extra_params.enable_gender_sim;
            delete extra_params['enable_gender_sim'];
        }
    }
    return {
        ...extra_params,
        ...formatted
    };
}


export { DirectLoginManager };
